// 输入条：左边是查哪些资料、怎么回答，右边是模型和发送。Enter 发送，Shift+Enter 换行；最多长到 8 行。

import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Dropdown, type MenuProps } from 'antd'
import { ArrowUp, AtSign, Check, ChevronDown, FileText, Lightbulb, Square, Telescope, X } from 'lucide-react'
import { listAgents, listChatModels } from '../../api/chat'
import { listKnowledgeBases } from '../../api/kb'
import { modelLabel } from '../../api/settings'
import type { Agent, Knowledge, KnowledgeBase } from '../../api/types'
import { AttachPicker } from '../kb/AttachPicker'
import { docTitle } from '../kb/DocCard'
import { IconButton, Pill } from '../../ui'
import type { ChatPrefs, Scope } from './prefs'
import { MicButton, useDictation } from '../voice/VoiceInput'
import './chat.css'


/**
 * 能用的回答方式：配好了模型的智能体。灵智原生内置的几个智能体没配模型，选了服务端会回「chat model is not configured」
 * （2026-10-11 上线时在正式服务器上碰到的）。一个都没有时不指定智能体，灵智原生用默认模型回答。
 */
export function useAgents() {
  const q = useQuery({ queryKey: ['agents'], queryFn: listAgents, staleTime: 5 * 60_000 })
  return (q.data ?? []).filter((a) => !!a.config?.model_id)
}
/** 选中的回答方式：记住的那个还能用就用它，否则第一个能用的。 */
export const pickAgent = (agents: Agent[], id?: string) => agents.find((a) => a.id === id) ?? agents[0]
export const useKnowledgeBases = () => useQuery({ queryKey: ['knowledge-bases'], queryFn: listKnowledgeBases, staleTime: 60_000 })
/** 「全部知识库」查的是哪些：不含问答库。还没载入时是 undefined。 */
export function useAllScopeKbs(): KnowledgeBase[] | undefined {
  return useKnowledgeBases().data?.filter((k) => k.type !== 'faq')
}

export function scopeLabel(scope: Scope, kbs: KnowledgeBase[]): string {
  switch (scope.kind) {
    case 'all':
      return kbs.length === 1 ? kbs[0].name : '全部知识库'
    case 'kb':
      return kbs.find((k) => k.id === scope.kbId)?.name ?? '知识库'
    case 'doc':
      return scope.title
    case 'none':
      return '不查资料'
  }
}

const check = (on: boolean) => <Check className="lucide" width={14} height={14} style={{ visibility: on ? 'visible' : 'hidden' }} />

/** 思考讨论：同一个输入框，按下「思考讨论」就换成要决定的问题（几个视角拿资料辩一辩，最后由你下判断）。 */
export interface ThinkMode {
  on: boolean
  onToggle: (on: boolean) => void
  attached: Knowledge[]
  onAttached: (k: Knowledge[]) => void
  max: number
  /** 有相关的旧判断时的一行提示（「过去的你」会一起来）。 */
  hint?: string | null
}

/** 深入研究：同一个输入框，按下就把这个问题拆开分头查，写成一份带出处的报告。 */
export interface ResearchToggle {
  on: boolean
  onToggle: (on: boolean) => void
}

interface ComposerProps {
  prefs: ChatPrefs
  onPrefs: (p: ChatPrefs) => void
  onSend: (query: string) => void
  onStop?: () => void
  busy?: boolean
  size?: 'md' | 'lg'
  placeholder?: string
  autoFocus?: boolean
  think?: ThinkMode
  research?: ResearchToggle
  /** 输入框里一开始的字（从别处带来的问题）。 */
  initialText?: string
  onText?: (text: string) => void
}

export function Composer({ prefs, onPrefs, onSend, onStop, busy, size = 'md', placeholder = '问问你的资料，或者直接聊…', autoFocus, think, research, initialText, onText }: ComposerProps) {
  const [text, setTextState] = useState(initialText ?? '')
  const setText = (t: string) => {
    setTextState(t)
    onText?.(t)
  }
  const thinking = !!think?.on
  const researching = !thinking && !!research?.on
  const box = useRef<HTMLTextAreaElement>(null)
  const kbs = useKnowledgeBases().data ?? []
  const allKbs = useAllScopeKbs() ?? []
  const agents = useAgents()
  const models = useQuery({ queryKey: ['chat-models'], queryFn: listChatModels, staleTime: 5 * 60_000 }).data ?? []
  const agent = pickAgent(agents, prefs.agentId)
  const model = models.find((m) => m.id === prefs.modelId)
  const voice = useDictation({ value: text, onChange: setText, inputRef: box })

  // 随内容长高
  useEffect(() => {
    const el = box.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [text])

  const submit = async () => {
    if (busy) return
    // 边说边点发送：等最后几个字进来再发
    const q = (voice.active ? await voice.stop() : text).trim()
    if (!q) return
    onSend(q)
    // 开始讨论、研究会换到那一页；没开成（出错）时问题还留在框里
    if (!thinking && !researching) setText('')
  }
  const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      void submit()
    }
  }

  // 思考讨论总要拿资料辩：范围只有全部知识库或某一个库；深入研究也总要查资料（「不查资料」按全部知识库）
  const scope: Scope = thinking ? thinkScope(prefs.scope) : researching && prefs.scope.kind === 'none' ? { kind: 'all' } : prefs.scope
  const scopeItems: MenuProps['items'] = [
    { key: 'all', label: <span className="pick-item">{check(scope.kind === 'all')}全部知识库</span> },
    ...kbs.filter((kb) => !thinking || kb.type !== 'faq').map((kb) => ({ key: `kb:${kb.id}`, label: <span className="pick-item">{check(scope.kind === 'kb' && scope.kbId === kb.id)}{kb.name}</span> })),
    ...(thinking || researching ? [] : [{ type: 'divider' as const }, { key: 'none', label: <span className="pick-item">{check(scope.kind === 'none')}不查资料，直接聊</span> }]),
  ]
  const onScope: MenuProps['onClick'] = ({ key }) => {
    const scope: Scope = key === 'all' ? { kind: 'all' } : key === 'none' ? { kind: 'none' } : { kind: 'kb', kbId: key.slice(3) }
    onPrefs({ ...prefs, scope })
  }
  const agentItems: MenuProps['items'] = agents.length
    ? agents.map((a) => ({ key: a.id, label: <span className="pick-item">{check(a.id === agent?.id)}{a.name}</span> }))
    : [{ key: '', label: <span className="pick-item">{check(true)}快速问答</span> }]
  const modelItems: MenuProps['items'] = [
    { key: '', label: <span className="pick-item">{check(!model)}默认模型</span> },
    ...models.map((m) => ({ key: m.id, label: <span className="pick-item">{check(m.id === model?.id)}{modelLabel(models, m)}</span> })),
  ]

  return (
    <>
    <div className="composer" data-size={size} data-think={thinking} data-research={researching}>
      {thinking && think!.attached.length > 0 && (
        <div className="composer-chips">
          {think!.attached.map((k) => (
            <span key={k.id} className="sk-chip" style={{ height: 26 }}>
              <FileText className="lucide" width={13} height={13} />
              {docTitle(k)}
              <IconButton label={`不用「${docTitle(k)}」`} style={{ width: 18, height: 18 }} onClick={() => think!.onAttached(think!.attached.filter((x) => x.id !== k.id))}>
                <X width={12} height={12} />
              </IconButton>
            </span>
          ))}
        </div>
      )}
      {!thinking && prefs.scope.kind === 'doc' && (
        <div className="composer-chips">
          <span className="sk-chip" style={{ height: 26 }}>
            <FileText className="lucide" width={13} height={13} />
            只问这一篇：{prefs.scope.title}
            <IconButton label="不再只问这一篇" style={{ width: 18, height: 18 }} onClick={() => onPrefs({ ...prefs, scope: { kind: 'all' } })}>
              <X width={12} height={12} />
            </IconButton>
          </span>
        </div>
      )}
      <label className="sr-only" htmlFor="composer-input">{thinking ? '你要决定什么' : researching ? '要研究的问题' : '提问'}</label>
      <textarea id="composer-input" ref={box} rows={1} value={text} placeholder={thinking ? '你要决定什么？' : researching ? '想把什么查清楚？可以写长一点' : placeholder} onChange={(e) => setText(e.target.value)} onKeyDown={onKey} autoFocus={autoFocus} readOnly={voice.active} data-dictating={voice.active} />
      <div className="composer-row">
        {(thinking || prefs.scope.kind !== 'doc') && (
          <Dropdown trigger={['click']} menu={{ items: scopeItems, onClick: onScope }}>
            <Pill size="sm" variant="selected" className="pick-trigger" icon={<AtSign />} aria-label={`资料范围：${scopeLabel(scope, scope.kind === 'all' ? allKbs : kbs)}`}>
              {scopeLabel(scope, scope.kind === 'all' ? allKbs : kbs)}
              <ChevronDown />
            </Pill>
          </Dropdown>
        )}
        {think && (
          <Pill size="sm" className="pick-trigger think-toggle" aria-pressed={thinking} icon={<Lightbulb />} onClick={() => think.onToggle(!thinking)} title="几个视角拿你的资料辩一辩，最后由你下判断">
            思考讨论
          </Pill>
        )}
        {research && !thinking && (
          <Pill size="sm" className="pick-trigger research-toggle" aria-pressed={researching} icon={<Telescope />} onClick={() => research.onToggle(!researching)} title="拆成几个小问题分头查你的资料，再写成一份带出处的报告（约 1 分钟）">
            深入研究
          </Pill>
        )}
        {thinking && <AttachPicker picked={think!.attached} onPick={(k) => think!.onAttached([...think!.attached, k])} max={think!.max} label="添加资料" placement="topRight" />}
        {!thinking && !researching && (
          <Dropdown trigger={['click']} menu={{ items: agentItems, onClick: ({ key }) => onPrefs({ ...prefs, agentId: key || undefined }) }}>
            <Pill size="sm" className="pick-trigger" aria-label={`回答方式：${agent?.name ?? '快速问答'}`}>
              {agent?.name ?? '快速问答'}
              <ChevronDown />
            </Pill>
          </Dropdown>
        )}
        {/* 模型、麦克风、发送是一组：窄屏折到下一行时也靠右 */}
        <span className="composer-end">
          {!thinking && !researching && models.length > 0 && (
            <Dropdown trigger={['click']} menu={{ items: modelItems, onClick: ({ key }) => onPrefs({ ...prefs, modelId: key || undefined }) }}>
              <Pill size="sm" variant="ghost" className="pick-trigger" aria-label={`模型：${model ? modelLabel(models, model) : '默认模型'}`}>
                {model ? modelLabel(models, model) : '默认模型'}
                <ChevronDown />
              </Pill>
            </Dropdown>
          )}
          <MicButton d={voice} />
          {thinking ? (
            <Pill size="sm" className="think-go" onClick={() => void submit()} disabled={(!text.trim() && !voice.active) || busy} aria-busy={busy}>
              开始讨论
            </Pill>
          ) : researching ? (
            <Pill size="sm" className="research-go" onClick={() => void submit()} disabled={(!text.trim() && !voice.active) || busy} aria-busy={busy}>
              开始研究
            </Pill>
          ) : busy ? (
            <IconButton label="停止生成" variant="primary" onClick={onStop}>
              <Square fill="currentColor" width={13} height={13} />
            </IconButton>
          ) : (
            <IconButton label="发送" variant="primary" onClick={() => void submit()} disabled={!text.trim() && !voice.active}>
              <ArrowUp strokeWidth={2.2} />
            </IconButton>
          )}
        </span>
      </div>
    </div>
    {thinking && think!.hint && (
      <p className="think-hint">
        <i aria-hidden="true" />
        {think!.hint}
      </p>
    )}
    </>
  )
}

/** 思考讨论用的范围：选了某个库就只用它，其他（全部、只问这一篇、不查资料）都按全部知识库。 */
export function thinkScope(scope: Scope): Scope {
  return scope.kind === 'kb' ? scope : { kind: 'all' }
}
